import { formatClassCount } from '@/helpers/credits';
import { isSinglePayment, withPlanClassesTerm } from '@/helpers/subscription';
import { formatPrice, formatRecurringPrice } from '@/lib/utils';
import type { SubscriptionCode } from '@/state/subscription-codes';
import type {
  Subscription,
  SubscriptionGroup,
} from '@/state/subscription-groups';

// A switch buys the target through checkout, where a member who already holds
// a membership is refused a new-customers-only plan; a change moves the Stripe
// price in place and never meets that gate.
export type PlanChoicePurpose = 'change' | 'switch';

type PlanTerms = Parameters<typeof withPlanClassesTerm>[1];

// The plan picker's and the plan-change summaries' wording: "10 classes/mo",
// or "10 classes for 1 month" for one payment, which never renews.
export const formatClassesLine = (
  classesPerCycle: number | null,
  plan: PlanTerms
): string =>
  withPlanClassesTerm(
    classesPerCycle == null
      ? 'Unlimited classes'
      : formatClassCount(classesPerCycle),
    plan
  );

export const formatPlanPrice = (
  plan: PlanTerms,
  price: number | null | undefined,
  currency: string | null | undefined
): string =>
  isSinglePayment(plan)
    ? formatPrice(price, currency)
    : formatRecurringPrice(
        price,
        currency,
        plan.charge_every_count,
        plan.charge_every_interval
      );

export const formatPlanTerms = (
  plan: PlanTerms &
    Pick<Subscription, 'price' | 'total_number_of_classes_per_cycle'>,
  currency: string | null | undefined
): string =>
  `${formatPlanPrice(plan, plan.price, currency)} · ${formatClassesLine(
    plan.total_number_of_classes_per_cycle,
    plan
  )}`;

// The member's own plan as the picker's locked "Current" row, built from the
// code rather than the catalog: a retired, private or bespoke plan is off the
// public list, and the member still needs to see what she is moving from.
const currentPlanRow = (subscriptionCode: SubscriptionCode): Subscription => ({
  ...subscriptionCode.subscription,
  status: 'INACTIVE',
  availability: 'PRIVATE',
  order: null,
  short_description: '',
  description: null,
  slug: '',
  one_time_purchase_only: false,
  new_customers_only: false,
  is_popular: false,
});

export const selectSwitchablePlans = (
  groups: SubscriptionGroup[] | undefined,
  subscriptionCode: SubscriptionCode,
  purpose: PlanChoicePurpose = 'change'
): Subscription[] => {
  if (!groups) return [];

  const currentSub = subscriptionCode.subscription;
  // By the group's id rather than by finding the current plan in it: the
  // public catalog lists only sellable plans, so a member on a retired plan
  // would otherwise find no group and lose every plan she could move to.
  const group = groups.find(
    (g) => g.subscription_group_id === currentSub.subscription_group_id
  );
  if (!group) return [];

  const isListed = group.subscriptions.some(
    (s) => s.subscription_id === subscriptionCode.subscription_id
  );
  const candidates = isListed
    ? group.subscriptions
    : [currentPlanRow(subscriptionCode), ...group.subscriptions];

  return candidates
    .filter(
      (s) =>
        s.subscription_id === subscriptionCode.subscription_id ||
        (s.status === 'ACTIVE' &&
          !s.is_custom &&
          // A free target makes a zero-total order, which never opens a Stripe
          // session, so the old subscription would never be closed. The backend
          // refuses one with PLAN_NOT_SWITCHABLE.
          !!s.price &&
          (purpose === 'change' || !s.new_customers_only) &&
          s.charge_every_count === currentSub.charge_every_count &&
          s.charge_every_interval === currentSub.charge_every_interval)
    )
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
};

export const hasOtherPlans = (
  groups: SubscriptionGroup[] | undefined,
  subscriptionCode: SubscriptionCode,
  purpose: PlanChoicePurpose = 'change'
): boolean =>
  selectSwitchablePlans(groups, subscriptionCode, purpose).some(
    (p) => p.subscription_id !== subscriptionCode.subscription_id
  );
